import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  ConfirmDialog,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  PageHeader,
  SkeletonRows,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  toast,
} from '@bia-auction/ui';
import { t } from '@bia-auction/shared-i18n';
import type { ApiKeyCreated, ApiKeySummary, Company } from '@bia-auction/shared-schema';
import { apiKeyCreatedSchema, apiKeySummarySchema, companySchema } from '@bia-auction/shared-schema';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { Archive, Ban, CheckCircle2 } from 'lucide-react';
import { useState } from 'react';
import { z } from 'zod';
import { ErrorMsg } from '../components/Feedback';
import { SecretOnce } from '../components/SecretOnce';
import { api, ApiError } from '../lib/api';
import { formatDateTime } from '../lib/format';

/**
 * One firm: its record, and the **ingestion API keys** it authenticates with (M13, ADR 0026).
 * This page is where a partner's credential comes from — an admin mints it here and hands the
 * plaintext over, because the firm has no self-service surface (and the plaintext is
 * unrecoverable afterwards: only its SHA-256 hash is stored).
 */
export function FirmDetailPage() {
  const { companyId = '' } = useParams({ strict: false });
  const qc = useQueryClient();
  const [minting, setMinting] = useState(false);
  const [label, setLabel] = useState('');
  const [issued, setIssued] = useState<ApiKeyCreated | null>(null);
  const [toRevoke, setToRevoke] = useState<ApiKeySummary | null>(null);
  const [toArchive, setToArchive] = useState(false);

  const firm = useQuery({
    queryKey: ['company', companyId],
    queryFn: () => api(`/admin/companies/${companyId}`, {}, companySchema),
  });
  const keys = useQuery({
    queryKey: ['company-keys', companyId],
    queryFn: () =>
      api(`/admin/companies/${companyId}/api-keys`, {}, z.array(apiKeySummarySchema)),
  });

  const fail = (e: unknown) => toast(e instanceof ApiError ? e.message : t('common.error'), 'error');
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['company-keys', companyId] });
    void qc.invalidateQueries({ queryKey: ['company', companyId] });
    void qc.invalidateQueries({ queryKey: ['companies'] });
  };

  const mint = useMutation({
    mutationFn: () =>
      api(
        `/admin/companies/${companyId}/api-keys`,
        { method: 'POST', body: { ...(label.trim() ? { label: label.trim() } : {}) } },
        apiKeyCreatedSchema,
      ),
    onSuccess: (created) => {
      // Close the label prompt and open the one-time reveal — the operator must not be able to
      // navigate past this without seeing the key.
      setMinting(false);
      setLabel('');
      setIssued(created);
      refresh();
    },
    onError: fail,
  });

  const revoke = useMutation({
    mutationFn: (keyId: string) =>
      api<void>(`/admin/companies/${companyId}/api-keys/${keyId}`, { method: 'DELETE' }),
    onSuccess: () => {
      toast(t('firms.keys.revoked'), 'success');
      setToRevoke(null);
      refresh();
    },
    onError: (e) => {
      setToRevoke(null);
      fail(e);
    },
  });

  const archive = useMutation({
    mutationFn: (archived: boolean) =>
      api<Company>(`/admin/companies/${companyId}/${archived ? 'restore' : 'archive'}`, {
        method: 'POST',
      }),
    onSuccess: (_r, archived) => {
      toast(t(archived ? 'firms.restored' : 'firms.archived'), 'success');
      setToArchive(false);
      refresh();
    },
    onError: (e) => {
      setToArchive(false);
      fail(e);
    },
  });

  if (firm.isLoading) return <SkeletonRows rows={4} />;
  if (firm.isError) return <ErrorMsg error={firm.error} />;
  if (!firm.data) return <p className="text-sm text-muted-foreground">{t('firms.notFound')}</p>;

  const f = firm.data;
  const isArchived = f.archivedAt !== null;

  return (
    <div className="space-y-4">
      <PageHeader title={f.name}>
        {/* One primary action per view (docs/15 §8): issuing a key is the job this page exists
            for, so archive/restore is the outlined secondary. */}
        <Button
          size="sm"
          onClick={() => setMinting(true)}
          disabled={isArchived}
          title={isArchived ? t('firms.keys.archivedNote') : undefined}
        >
          {t('firms.keys.new')}
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => (isArchived ? archive.mutate(true) : setToArchive(true))}
          disabled={archive.isPending}
        >
          {isArchived ? t('firms.restore') : t('firms.archive')}
        </Button>
      </PageHeader>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {t('firms.title')}
            {isArchived && (
              <Badge variant="secondary">
                <Archive aria-hidden className="mr-1 h-3 w-3" />
                {t('firms.archivedBadge')}
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="grid grid-cols-2 gap-3 text-sm">
            <Field label={t('firms.field.taxAdmin')} value={f.taxAdmin} />
            <Field label={t('firms.field.taxNumber')} value={f.taxNumber} numeric />
            <Field label={t('firms.field.contactEmail')} value={f.contactEmail} />
            <Field label={t('firms.field.contactPhone')} value={f.contactPhone} numeric />
            <Field label={t('firms.field.address')} value={f.address} />
          </dl>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('firms.keys.title')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">{t('firms.keys.intro')}</p>
          {isArchived && (
            <p className="text-sm text-muted-foreground">{t('firms.restoreNote')}</p>
          )}
          {keys.isLoading ? (
            <SkeletonRows rows={2} />
          ) : keys.isError ? (
            <ErrorMsg error={keys.error} />
          ) : (keys.data ?? []).length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('firms.keys.empty')}</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('firms.keys.col.label')}</TableHead>
                  <TableHead>{t('firms.keys.col.created')}</TableHead>
                  <TableHead>{t('firms.keys.col.lastUsed')}</TableHead>
                  <TableHead>{t('firms.keys.col.state')}</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {(keys.data ?? []).map((k) => (
                  <TableRow key={k.id}>
                    <TableCell>{k.label ?? '—'}</TableCell>
                    <TableCell className="tabular-nums">{formatDateTime(k.createdAt)}</TableCell>
                    <TableCell className="tabular-nums">
                      {k.lastUsedAt === null
                        ? t('firms.keys.neverUsed')
                        : formatDateTime(k.lastUsedAt)}
                    </TableCell>
                    <TableCell>
                      {/* Icon + label, never colour alone (docs/15 §2) — "revoked" is the
                          state an operator must not misread at a glance. */}
                      <Badge variant={k.revokedAt === null ? 'success' : 'secondary'}>
                        {k.revokedAt === null ? (
                          <CheckCircle2 aria-hidden className="mr-1 h-3 w-3" />
                        ) : (
                          <Ban aria-hidden className="mr-1 h-3 w-3" />
                        )}
                        {t(k.revokedAt === null ? 'firms.keys.state.active' : 'firms.keys.state.revoked')}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      {k.revokedAt === null && (
                        <Button variant="ghost" size="sm" onClick={() => setToRevoke(k)}>
                          {t('firms.keys.revoke')}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Mint: label first, then the one-time reveal. */}
      <Dialog open={minting} onOpenChange={(o) => !o && setMinting(false)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('firms.keys.new')}</DialogTitle>
          </DialogHeader>
          <form
            className="space-y-3"
            onSubmit={(e) => {
              e.preventDefault();
              mint.mutate();
            }}
          >
            <div className="space-y-1.5">
              <Label htmlFor="key-label">{t('firms.keys.label')}</Label>
              <Input
                id="key-label"
                value={label}
                placeholder={t('firms.keys.labelPlaceholder')}
                onChange={(e) => setLabel(e.target.value)}
              />
            </div>
            <DialogFooter>
              <Button type="submit" disabled={mint.isPending}>
                {t('firms.keys.new')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={issued !== null} onOpenChange={(o) => !o && setIssued(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('firms.keys.createdTitle')}</DialogTitle>
          </DialogHeader>
          {issued && <SecretOnce value={issued.key} warning={t('firms.keys.createdWarning')} />}
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={toRevoke !== null}
        onOpenChange={(o) => !o && setToRevoke(null)}
        title={t('firms.keys.revoke')}
        description={t('firms.keys.revokeConfirm')}
        confirmLabel={t('firms.keys.revoke')}
        cancelLabel={t('common.cancel')}
        destructive
        busy={revoke.isPending}
        onConfirm={() => toRevoke && revoke.mutate(toRevoke.id)}
      />

      <ConfirmDialog
        open={toArchive}
        onOpenChange={(o) => !o && setToArchive(false)}
        title={t('firms.archive')}
        description={t('firms.archiveConfirm', { name: f.name })}
        confirmLabel={t('firms.archive')}
        cancelLabel={t('common.cancel')}
        destructive
        busy={archive.isPending}
        onConfirm={() => archive.mutate(false)}
      />
    </div>
  );
}

function Field({
  label,
  value,
  numeric,
}: {
  label: string;
  value: string | null;
  numeric?: boolean;
}) {
  return (
    <div>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className={numeric ? 'tabular-nums' : undefined}>{value ?? '—'}</dd>
    </div>
  );
}
